/**
 * The model behind the per-card statement panel (CardStatementPanel), derived
 * from data this page already holds: the card's StatementPayment rows, the
 * SchedulePayment rows drawn FROM the card, and the account's own limit / APR.
 *
 * Kept out of the components on purpose. Every figure here is a small piece of
 * arithmetic over dates that are easy to get subtly wrong (grace, overdue,
 * paid-so-far), and having them in one pure function is what lets the panel
 * stay a renderer.
 *
 * Nothing here feeds the cash projection. The card payments ARE already an
 * outflow on the paying clinic's grid (see statementFlowsByClinic) and the
 * charges are the card's own; this module only re-presents them.
 */

import { occurrencesInRange } from "./scheduleOccurrences";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Whole days from `a` to `b`, both ISO. Parsed at local midnight — a bare ISO
 *  string is read as UTC by JS, which lands on the previous day west of
 *  Greenwich and would report an off-by-one "days overdue". */
export function daysBetween(a, b) {
  if (!a || !b) return null;
  const from = new Date(`${a}T00:00:00`);
  const to = new Date(`${b}T00:00:00`);
  return Math.round((to - from) / 86400000);
}

/** "Jun 24" — the short form used everywhere on this panel. */
export function fmtShortDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`;
}

/** "Jun 2026" — the month a statement (or a pending one) belongs to. */
export function fmtMonthYear(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return `${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * How the balance is broken up, in words: "Spread over 3 business days".
 * Falls back to the raw plan value so an unrecognised plan still says
 * something true rather than nothing.
 */
function planLabel(plan, count, display) {
  const n = Number(count) || 0;
  switch (plan) {
    case "full_on_due":
      return "Full balance on the due date";
    case "single_payment":
      return "Single payment";
    case "business_days":
      return n > 1 ? `Spread over ${n} business days` : "Paid on one business day";
    case "weekly_installments":
      return n > 1 ? `${n} weekly installments` : "One weekly installment";
    case "custom_split":
      return "Custom split";
    default:
      // An unrecognised plan still has the server's own wording to fall back
      // on, which beats printing a raw enum value at someone.
      return display || (plan ? String(plan).replace(/_/g, " ") : null);
  }
}

/**
 * The statement this panel is about: the one whose [issued .. due] span overlaps
 * the visible window, latest issue date first.
 *
 * Overlap rather than "issued this month" because the two questions have
 * different answers exactly when they matter most — a statement issued Jun 3 and
 * due Jul 24 is the live one for the whole of July, and picking by issue date
 * would show July's grid an empty panel while that balance is still owed.
 */
function pickStatement(statements, from, to) {
  const inView = (statements ?? []).filter(
    (st) =>
      st.statement_date &&
      st.due_date &&
      st.statement_date <= to &&
      st.due_date >= from &&
      st.status !== "cancelled" &&
      st.status !== "paused",
  );
  if (!inView.length) return null;
  return inView.sort((a, b) => String(b.statement_date).localeCompare(String(a.statement_date)))[0];
}

/**
 * Charges billed to the card itself — the SchedulePayment rows whose paying
 * account IS the card. These are what make the NEXT statement grow, so they are
 * read over the current statement's [issued .. due] span rather than the visible
 * window: that is the period "will this card go over its limit before I pay it"
 * is asked about.
 */
function buildCharges(schedules, card, start, end) {
  const out = [];
  (schedules ?? []).forEach((sp) => {
    if (sp.bank_account !== card.id) return;
    occurrencesInRange(sp, start, end).forEach((occ) => {
      const amount = Number(occ.amount);
      if (!Number.isFinite(amount) || amount === 0) return;
      out.push({
        key: `${sp.id}-${occ.date}`,
        // What an editor opens: the SERIES this charge came from. A charge has
        // no row of its own — it is one occurrence generated from the schedule
        // — so the schedule is the only thing there is to edit.
        scheduleId: sp.id,
        // The schedule's own note is the closest thing to a payee, and it is
        // what whoever entered the row wrote to identify it. Blank notes fall
        // back to the category alone rather than rendering an empty line.
        vendor: (sp.notes || "").trim() || null,
        category: sp.expense_type_display || sp.expense_type || "Charge",
        date: occ.date,
        // Day of the month, which is how a recurring charge is actually thought
        // about ("the 2nd") — the full date is on the row's title attribute.
        day: Number(occ.date.slice(8, 10)),
        amount,
      });
    });
  });
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/** The most recent entered card balance on or before `today` — what is actually
 *  drawn on the card right now. Null when nothing has been entered yet. */
function balanceAsOf(balanceByDay, today) {
  let bestDate = null;
  Object.keys(balanceByDay ?? {}).forEach((d) => {
    if (today && d > today) return;
    if (bestDate === null || d > bestDate) bestDate = d;
  });
  return bestDate === null ? null : (balanceByDay[bestDate]?.amount ?? null);
}

/**
 * How much of the card's limit is in use, and where that is heading before the
 * balance comes due.
 *
 * `used` is the card's OWN balance — the entered figure the Balance row shows,
 * i.e. what is really drawn against the limit. It is deliberately not the
 * statement's unpaid amount: a statement is one month's bill, while the limit is
 * consumed by everything on the card, including charges made since it was
 * issued and older balances not yet billed.
 *
 * `peak` then rolls that forward to the due date — charges add, payments
 * subtract — and keeps the highest point reached. That is the moment the card
 * would be declined, which is the question a limit is asked about.
 *
 * With no balance ever entered for the card there is nothing to measure, and the
 * whole block is dropped rather than shown against a guess.
 */
function buildUtilisation({ balanceByDay, charges, payments, limit, today, dueDate }) {
  if (!limit || limit <= 0) return null;
  const used = balanceAsOf(balanceByDay, today);
  if (used == null) return null;

  // Everything still ahead of us and inside the cycle, in the order it happens.
  // Strictly after today: today's movements are already in the entered balance.
  const ahead = (d) => (!today || d > today) && (!dueDate || d <= dueDate);
  const events = [
    ...charges.filter((c) => ahead(c.date)).map((c) => ({ date: c.date, delta: c.amount })),
    ...payments.filter((p) => ahead(p.date)).map((p) => ({ date: p.date, delta: -p.amount })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  let running = used;
  let peak = used;
  // WHEN the peak falls, which is most of what makes it actionable. It is
  // rarely the due date and rarely after the last charge — payments interleave
  // with charges and pull the running total back down — so naming the day stops
  // the figure being read as "where the card ends up".
  let peakOn = today ?? null;
  let upcoming = 0;
  events.forEach((e) => {
    running += e.delta;
    if (e.delta > 0) upcoming += e.delta;
    if (running > peak) {
      peak = running;
      peakOn = e.date;
    }
  });

  return {
    used,
    limit,
    pct: used / limit,
    peak,
    peakPct: peak / limit,
    peakOn,
    upcoming,
    dueDate,
  };
}

/**
 * Build the panel model for one credit card.
 *
 * Returns `{ pending: true }` when no statement covers the window — the panel
 * then says the month's balance has not been entered yet, which is a real state
 * (the statement arrives days into the month) and not an error.
 */
export function buildCardStatementDetail({
  card,
  statements = [],
  schedules = [],
  // date → {amount} of the card's OWN entered balance, the same series the
  // Balance row draws. What the limit is measured against.
  balanceByDay = {},
  today,
  windowFrom,
  windowTo,
}) {
  if (!card || !windowFrom || !windowTo) return null;

  const st = pickStatement(statements, windowFrom, windowTo);
  if (!st) {
    return {
      pending: true,
      card,
      monthLabel: fmtMonthYear(windowFrom),
      // Even with no statement, charges are still hitting the card this month —
      // the one thing the panel CAN answer while the balance is unknown.
      charges: buildCharges(schedules, card, windowFrom, windowTo),
    };
  }

  const balance = Number(st.balance) || 0;
  const splits = (Array.isArray(st.splits) ? st.splits : [])
    .map((s, i) => ({
      index: i,
      date: s?.date ?? null,
      amount: Number(s?.amount) || 0,
    }))
    .filter((s) => s.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  // A split has no cleared flag of its own — StatementPayment records the plan,
  // not the bank's confirmation — so "paid" is read from the calendar: a payment
  // dated today or earlier has gone out.
  //
  // Inclusive of TODAY to match the Statement balance row on the grid, where a
  // payment reduces what is owed on the day it is made. The two describe the
  // same money, and a panel saying $1,000 is still to pay beside a cell already
  // showing $750 is worse than either rule on its own.
  const paidSoFar = splits
    .filter((s) => today && s.date <= today)
    .reduce((sum, s) => sum + s.amount, 0);
  const toPay = Math.max(balance - paidSoFar, 0);

  const graceDays = daysBetween(st.statement_date, st.due_date);
  const overdueDays =
    today && st.due_date < today && toPay > 0 ? daysBetween(st.due_date, today) : 0;

  // Over the whole visible window rather than the statement's own span: the
  // question this list answers is "what is this card being billed this month",
  // and a charge on the 2nd of a month whose statement is issued on the 3rd is
  // part of that answer. Which of them count against the limit before the due
  // date is buildUtilisation's narrower question.
  const charges = buildCharges(schedules, card, windowFrom, windowTo);

  return {
    pending: false,
    card,
    statementId: st.id,
    monthLabel: fmtMonthYear(st.statement_date),
    balance,
    paidSoFar,
    toPay,
    statementDate: st.statement_date,
    dueDate: st.due_date,
    graceDays,
    overdueDays,
    planLabel: planLabel(st.payment_plan, st.plan_count, st.payment_plan_display),
    payerLabel: st.from_bank_account_label ?? null,
    payments: splits.map((s) => ({
      ...s,
      count: splits.length,
      // "Paid" is read off the calendar, exactly as `paidSoFar` is — there is no
      // per-split clearance to consult — so the two can never disagree about
      // which payments make up the figure at the top of the panel.
      paid: !!(today && s.date <= today),
      // A payment dated after the balance was due is what makes the statement
      // late, and it is the one worth pointing at: the plan itself, not a
      // missed transfer, is why interest will be charged.
      afterDue: !!(st.due_date && s.date > st.due_date),
    })),
    charges,
    utilisation: buildUtilisation({
      balanceByDay,
      charges,
      payments: splits,
      limit: Number(card.creditLimit) || 0,
      today,
      dueDate: st.due_date,
    }),
  };
}
